/**
 * Loyalty Service SSE API
 * Based on OpenAPI 3.1.0 specification
 * All point values are in basis points (divide by 10,000 for display)
 */

import { getCurrentHostname } from '@/helpers/conf';

// ============ Base Types ============

export interface ILoyaltyBonus {
  id: number;
  title: string;
  internalTitle: string;
  groupKey: string;
  activated: string | null;
}

export interface ILoyaltyLevel {
  id: number;
  name: string;
  /** Basis points - divide by 10,000 for display */
  upgradePointAmount: number;
  /** Basis points - divide by 10,000 for display */
  retainPointAmount: number;
  retainPeriodDays: number;
  bonus: ILoyaltyBonus | null;
}

export interface ILoyaltyUserLevel {
  id: number;
  number: number;
  name: string;
}

export interface ILoyaltyHistoryEntry {
  fromLevel: string;
  toLevel: string;
  status: string;
  /** Basis points - divide by 10,000 for display */
  surplusPoints: number;
  /** When the user entered the source level period */
  startedAt: string;
  /** When the source level period finished */
  finishedAt: string;
}

// ============ SSE Event Types ============

export type ILoyaltySseEventType =
  | 'init'
  | 'progress'
  | 'upgrade'
  | 'downgrade'
  | 'retain'
  | 'bonus';

export interface ISSEInitEvent {
  externalUserId: string;
  level: ILoyaltyUserLevel;
  /** Basis points - divide by 10,000 for display */
  points: number;
  /** Basis points - divide by 10,000 for display. Null if at max level */
  pointsToNextLevel: number | null;
  retentionDeadline?: string;
  progressStartedAt: string;
  levels: ILoyaltyLevel[];
  history: ILoyaltyHistoryEntry[];
  timestamp: string;
}

export interface ISSEProgressEvent {
  /** Basis points - divide by 10,000 for display */
  points: number;
  /** Basis points - divide by 10,000 for display. Null if at max level */
  pointsToNextLevel: number | null;
  timestamp: string;
}

export interface ISSEUpgradeEvent {
  fromLevel: string;
  toLevel: string;
  /** Basis points - divide by 10,000 for display */
  surplusPoints: number;
  /** Basis points - divide by 10,000 for display */
  points: number;
  /** Basis points - divide by 10,000 for display */
  upgradePointAmount: number;
  /** Basis points - divide by 10,000 for display */
  retainPointAmount: number;
  retentionDeadline: string;
  progressStartedAt: string;
  timestamp: string;
}

export interface ISSEDowngradeEvent {
  fromLevel: string;
  toLevel: string;
  /** Basis points - divide by 10,000 for display */
  points: number;
  /** Basis points - divide by 10,000 for display */
  upgradePointAmount: number;
  /** Basis points - divide by 10,000 for display */
  retainPointAmount: number;
  retentionDeadline: string;
  progressStartedAt: string;
  timestamp: string;
}

export interface ISSEBonusEvent {
  bonusId: number;
  internalTitle: string;
  levelId: number;
  externalBonusId: number;
  activated: string;
  timestamp: string;
}

export interface ISSERetainEvent {
  level: string;
  /** Basis points - divide by 10,000 for display */
  points: number;
  /** Basis points - divide by 10,000 for display */
  upgradePointAmount: number;
  /** Basis points - divide by 10,000 for display */
  retainPointAmount: number;
  retentionDeadline: string;
  progressStartedAt: string;
  timestamp: string;
}

export type ILoyaltySseEventData =
  | { type: 'init'; data: ISSEInitEvent }
  | { type: 'progress'; data: ISSEProgressEvent }
  | { type: 'upgrade'; data: ISSEUpgradeEvent }
  | { type: 'downgrade'; data: ISSEDowngradeEvent }
  | { type: 'retain'; data: ISSERetainEvent }
  | { type: 'bonus'; data: ISSEBonusEvent };

// ============ SSE Event Handlers ============

export interface ILoyaltySseEventHandlers {
  onInit?: (data: ISSEInitEvent) => void;
  onProgress?: (data: ISSEProgressEvent) => void;
  onUpgrade?: (data: ISSEUpgradeEvent) => void;
  onDowngrade?: (data: ISSEDowngradeEvent) => void;
  onRetain?: (data: ISSERetainEvent) => void;
  onBonus?: (data: ISSEBonusEvent) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
}

// ============ Constants ============

const hostname =
  typeof window !== 'undefined'
    ? getCurrentHostname(window.location.hostname)
    : '';

const LOYALTY_DEV_PREFIX = 'https://loyalty-lv01-dev.';
const LOYALTY_PROD_PREFIX = 'https://loyalty-lv02-prod.';
const isDevEnv = process.env.NEXT_PUBLIC_APP_ENV === 'development';
const LOYALTY_PREFIX = isDevEnv ? LOYALTY_DEV_PREFIX : LOYALTY_PROD_PREFIX;

const LOYALTY_SSE_SUBSCRIBE_URL = `${LOYALTY_PREFIX}${hostname}/api/v1/user/subscribe`;
// const LOYALTY_SSE_SUBSCRIBE_URL = '/api/internal/loyalty-sse';

// ============ SSE Subscription ============

export function subscribeLoyaltySse(
  handlers: ILoyaltySseEventHandlers
): () => void {
  const eventSource = new EventSource(LOYALTY_SSE_SUBSCRIBE_URL, {
    withCredentials: true,
  });

  eventSource.onopen = () => {
    handlers.onOpen?.();
  };

  eventSource.onerror = (error) => {
    handlers.onError?.(error);
  };

  eventSource.addEventListener('init', (event) => {
    try {
      const data = JSON.parse(event.data) as ISSEInitEvent;
      handlers.onInit?.(data);
    } catch (error) {
      handlers.onError?.(error as Event);
    }
  });

  eventSource.addEventListener('progress', (event) => {
    const data = JSON.parse(event.data) as ISSEProgressEvent;
    handlers.onProgress?.(data);
  });

  eventSource.addEventListener('upgrade', (event) => {
    const data = JSON.parse(event.data) as ISSEUpgradeEvent;
    handlers.onUpgrade?.(data);
  });

  eventSource.addEventListener('downgrade', (event) => {
    const data = JSON.parse(event.data) as ISSEDowngradeEvent;
    handlers.onDowngrade?.(data);
  });

  eventSource.addEventListener('retain', (event) => {
    const data = JSON.parse(event.data) as ISSERetainEvent;
    handlers.onRetain?.(data);
  });

  eventSource.addEventListener('bonus', (event) => {
    const data = JSON.parse(event.data) as ISSEBonusEvent;
    handlers.onBonus?.(data);
  });

  return () => {
    eventSource.close();
  };
}

// ============ Utility Functions ============

/**
 * Convert basis points to display value
 * @param basisPoints - Points in basis points format
 * @returns Display value (divided by 10,000)
 */
export function basisPointsToDisplay(basisPoints: number): number {
  return basisPoints / 10_000;
}

/**
 * Convert display value to basis points
 * @param displayValue - Display value
 * @returns Points in basis points format (multiplied by 10,000)
 */
export function displayToBasisPoints(displayValue: number): number {
  return displayValue * 10_000;
}
